import { Data, Utils } from 'common';

const { PlanIds: PlanId } = Data;
const { Session } = Utils;

// Backend returns DateTime.MinValue for never-billed accounts. Anything else
// (a real billing date) means the customer has completed their trial month.
const NEVER_BILLED = '0001-01-01T00:00:00';

/**
 * A customer is in their free-trial billing period when they've never been
 * billed. This stays true across an in-trial Trial401 → Basic402 upgrade
 * because LastBilledDate only gets set when the first bill actually lands.
 */
export const isInTrialBillingPeriod = (): boolean => {
	const { LastBilledDate } =
		((Session as any).getInternalAccountInfo() as {
			LastBilledDate?: string;
		}) ?? {};
	return !LastBilledDate || LastBilledDate === NEVER_BILLED;
};

export const NEW_LINEUP_PLAN_IDS: number[] = [
	PlanId.Trial401,
	PlanId.Basic402,
	PlanId.Professional403,
	PlanId.MultiLocation404,
	PlanId.MultiLocation405,
	PlanId.MultiLocation406,
];

export const DEFAULT_SEAT_PRICE = 5;

export const getSeatPrice = (plan?: { AdditionalUserFee?: number }): number =>
	plan?.AdditionalUserFee ?? DEFAULT_SEAT_PRICE;

export const getIncludedSeats = (plan?: {
	NumberOfLocations?: number;
}): number => plan?.NumberOfLocations ?? 1;

export const isMultiLocationPlan = (planId: number): boolean => {
	return (
		planId === PlanId.EcommerceV383 ||
		planId === PlanId.Ecommerce ||
		planId === PlanId.MultiLocation404 ||
		planId === PlanId.MultiLocation405 ||
		planId === PlanId.MultiLocation406
	);
};

export const isPaygPlan = (planId: number): boolean => {
	return planId === PlanId.PAYG;
};

// Split on whitespace + dash, agnostic to how many spaces precede it (plans
// vary between one and three). Excludes dashes with a digit on either side
// (e.g. "5 - 10") so a numeric range in feature text isn't mistaken for a
// delimiter — every real delimiter here is preceded by punctuation/a word and
// followed by a capitalized word, never a digit.
const SUMMARY_DELIMITER = /(?<!\d)\s+-\s*(?!\d)/;

export const parsePlanFeatures = (summary: string): string[] => {
	const segments = summary
		? summary
				.split(SUMMARY_DELIMITER)
				.map(s => s.trim())
				.filter(t => t !== '')
		: [];
	// First two segments are plan tag and sub-header — skip them
	return segments.length > 2 ? segments.slice(2) : [];
};

export interface ParsedPlanSummary {
	tag: string;
	subHeader: string;
	features: string[];
}

export const parsePlanSummary = (summary: string): ParsedPlanSummary => {
	const segments = summary
		? summary
				.split(SUMMARY_DELIMITER)
				.map(s => s.trim())
				.filter(t => t !== '')
		: [];
	return {
		tag: segments[0] ?? '',
		subHeader: segments[1] ?? '',
		features: segments.slice(2),
	};
};

// Plans added after the GBB relaunch (PlanId > 374) carry their tag/subHeader/
// feature copy in Description rather than Summary.
const DESCRIPTION_SOURCE_MIN_PLAN_ID = 374;

export const getPlanSummarySource = (plan: {
	PlanId: number;
	Summary?: string;
	Description?: string;
}): string =>
	(plan.PlanId > DESCRIPTION_SOURCE_MIN_PLAN_ID
		? plan.Description
		: plan.Summary) ?? '';
